import axios from "axios";

const errorCodes = {
  badRequest: "badRequest",
  unauthorized: "unauthorized",
  forbidden: "forbidden",
  notFound: "notFound",
  serverError: "serverError",
  unexpected: "unexpected",
  invalidCredentials: "invalidCredentials",
};

class API {
  constructor(collection) {
    this.collectionUrl = `${process.env.REACT_APP_API_BASE_URL}/${collection}`;
  }

  _handleError(error) {
    if (error.response?.status === 400)
      return Promise.reject({
        code: errorCodes.badRequest,
        message: error.response.data,
      });
    if (error.response?.status === 401)
      return Promise.reject(errorCodes.unauthorized);
    if (error.response?.status === 403)
      return Promise.reject(errorCodes.forbidden);
    if (error.response?.status === 404)
      return Promise.reject(errorCodes.notFound);
    if (500 <= error.response?.status <= 599)
      return Promise.reject(errorCodes.serverError);
    return Promise.reject(errorCodes.unexpected);
  }

  async getMulti(
    ordering = null,
    orderKey = "ordering",
    search = null,
    limit = null,
    offset = null,
    extra = {}
  ) {
    const params = { limit, offset, ...extra };
    if (ordering) params[orderKey] = ordering;
    if (search && search.length > 0) params.search = search;
    try {
      const response = await axios.get(`${this.collectionUrl}/`, {
        params,
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return this._handleError(error);
    }
  }

  async getMultiRides(
    ordering = null,
    orderKey = "ordering",
    search = null,
    limit = null,
    offset = null,
    extra = {}
  ) {
    const params = new URLSearchParams();

    if (limit) params.append("limit", limit);
    if (offset) params.append("offset", offset);
    if (ordering) params.append(orderKey, ordering);
    if (search) params.append("search", search);

    Object.entries(extra).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, v));
      } else if (value !== undefined && value !== null) {
        params.append(key, value);
      }
    });

    try {
      const response = await axios.get(`${this.collectionUrl}/`, {
        params,
      });
      return response.data;
    } catch (error) {
      return this._handleError(error);
    }
  }

  async getOne(id) {
    try {
      const response = await axios.get(`${this.collectionUrl}/${id}/`);
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }

  async getDetails(id) {
    try {
      const response = await axios.get(`${this.collectionUrl}/${id}/details/`);
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }

  async updateDetails(id, data, partial = false) {
    try {
      let response = null;
      if (partial)
        response = await axios.patch(
          `${this.collectionUrl}/${id}/details`,
          data
        );
      else
        response = await axios.put(`${this.collectionUrl}/${id}/details`, data);
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }

  async downloadDetails(id) {
    try {
      const response = await axios.get(
        `${this.collectionUrl}/${id}/service-confirmation/`,
        { responseType: "blob" } // Indica que esperas un archivo binario
      );
      return response; // Retorna toda la respuesta, no solo response.data
    } catch (e) {
      return this._handleError(e);
    }
  }

  async updateStatus(data) {
    try {
      const response = await axios.post(
        `${this.collectionUrl}/update-status/`,
        data
      );
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }

  async getReport(filters = {}) {
    try {
      // Construir los parámetros de consulta a partir de los filtros
      const queryParams = new URLSearchParams(filters).toString();

      // Hacer la solicitud con los parámetros
      const response = await axios.get(
        `${this.collectionUrl}/report/?${queryParams}`
      );
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }

  async downloadReport(filters = {}, data) {
    try {
      // Construir los parámetros de consulta a partir de los filtros
      const queryParams = new URLSearchParams(filters).toString();

      // Hacer la solicitud con los parámetros
      const response = await axios.post(
        `${this.collectionUrl}/report/download/?${queryParams}`,
        data,
        {
          responseType: "blob", // Esto asegura que la respuesta se maneje como binaria
        }
      );

      return Promise.resolve(response); // Retornar directamente la respuesta completa
    } catch (e) {
      return this._handleError(e);
    }
  }

  async getDownloadReport(filters = {}) {
    try {
      // Construir los parámetros de consulta a partir de los filtros
      const queryParams = new URLSearchParams(filters).toString();

      // Hacer la solicitud con los parámetros
      const response = await axios.get(
        `${this.collectionUrl}/report/download/?${queryParams}`,
        {
          responseType: "blob", // Esto asegura que la respuesta se maneje como binaria
        }
      );

      return Promise.resolve(response); // Retornar directamente la respuesta completa
    } catch (e) {
      return this._handleError(e);
    }
  }

  async updateAvailability(data) {
    try {
      const response = await axios.post(
        `${this.collectionUrl}/availability/`,
        data
      );
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }

  async updateExChange(data) {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/exchange-rate/`,
        data
      );
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }

  async tripClosure(id, data) {
    try {
      const response = await axios.post(
        `${this.collectionUrl}/${id}/close/`,
        data
      );
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }

  async updatePassword(id, data) {
    try {
      const response = await axios.post(
        `${this.collectionUrl}/${id}/set-password/`,
        data
      );
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }

  async create(data) {
    try {
      const response = await axios.post(`${this.collectionUrl}/`, data);
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }

  async update(id, data, partial = false) {
    try {
      let response = null;
      if (partial)
        response = await axios.patch(`${this.collectionUrl}/${id}/`, data);
      else response = await axios.put(`${this.collectionUrl}/${id}/`, data);
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }

  async delete(id) {
    try {
      const response = await axios.delete(`${this.collectionUrl}/${id}/`);
      return response;
    } catch (e) {
      return this._handleError(e);
    }
  }
}

class UserAPI extends API {
  async getToken(username, password) {
    try {
      const response = await axios.post(`${this.collectionUrl}/token/`, {
        username,
        password,
      });
      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(errorCodes.invalidCredentials);
    }
  }

  async changePassword(newPass) {
    try {
      const response = await axios.post(
        `${this.collectionUrl}/me/change-password/`,
        { new: newPass }
      );
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }

  async getMe() {
    try {
      const response = await axios.get(`${this.collectionUrl}/me/`);
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }
}

class PasswordAPI extends API {
  async sendEmail(email) {
    try {
      const response = await axios.post(`${this.collectionUrl}/`, {
        email: email,
      });
      return Promise.resolve(response.data);
    } catch (e) {
      return this._handleError(e);
    }
  }

  async changePassword(password, uuid, token) {
    try {
      const response = await axios.post(
        `${this.collectionUrl}/${uuid}/${token}/`,
        { password: password }
      );
    } catch (e) {
      return this._handleError(e);
    }
  }
}

const api = {
  users: new UserAPI("users"),
  clients: new UserAPI("clients"),
  drivers: new UserAPI("drivers"),
  vehicles: new UserAPI("vehicles"),
  rides: new UserAPI("rides"),
  washes: new UserAPI("washes"),
  reviews: new UserAPI("reviews"),
  reset_password: new PasswordAPI("reset-password"),
};

export default api;
export { errorCodes, API, UserAPI };
